/**
 * AdminAnalyticsTab - Analytics & Insights dashboard component
 */
import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Download, Users, DollarSign, TrendingUp,
  Image, RefreshCw, Tag, BarChart3, Percent, UserPlus,
  Activity, Calendar, ShoppingCart, ArrowRight, ChevronRight, Clock, Timer, UserX
} from "lucide-react";
import { Button } from "@/components/ui/button";

function FunnelCard({ label, value, subLabel, icon: Icon, color, isLast }) {
  return (
    <div className="flex items-center">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex-1 min-w-[140px]" data-testid={`funnel-${label.replace(/\s/g, "-").toLowerCase()}`}>
        <div className="flex items-center justify-between mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
          {value}
        </p>
        <p className="text-xs font-medium text-slate-600 mt-1">{label}</p>
        {subLabel && (
          <p className="text-[10px] text-slate-400 mt-0.5">{subLabel}</p>
        )}
      </div>
      {!isLast && (
        <div className="flex-shrink-0 mx-2">
          <ChevronRight className="h-5 w-5 text-slate-300" />
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, subValue, icon: Icon, color, trend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{label}</p>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {value}
      </p>
      {subValue && (
        <p className="text-[10px] text-slate-400 mt-0.5">{subValue}</p>
      )}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-1 text-[10px] ${trend >= 0 ? "text-green-600" : "text-red-500"}`}>
          <TrendingUp className={`h-3 w-3 ${trend < 0 ? "rotate-180" : ""}`} />
          <span>{trend >= 0 ? "+" : ""}{trend}%</span>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-600" />
      </div>
      <h3 className="text-sm font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {title}
      </h3>
    </div>
  );
}

function TopItemsTable({ title, items, columns }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h4 className="text-xs font-semibold text-slate-600 mb-3">{title}</h4>
        <p className="text-xs text-slate-400 text-center py-4">No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h4 className="text-xs font-semibold text-slate-600">{title}</h4>
      </div>
      <div className="max-h-[280px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              {columns.map((col, i) => (
                <th key={i} className={`px-4 py-2 text-[10px] font-medium text-slate-500 ${col.align || "text-left"}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item, i) => (
              <tr key={i} className="hover:bg-slate-50/50">
                {columns.map((col, j) => (
                  <td key={j} className={`px-4 py-2 ${col.align || "text-left"} ${col.className || ""}`}>
                    {col.render ? col.render(item, i) : item[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await adminAPI.analytics();
      setData(res.data);
      if (showToast) toast.success("Analytics refreshed");
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Failed to load analytics</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => fetchAnalytics()}>
          Retry
        </Button>
      </div>
    );
  }

  const { funnel, usage, revenue, users, ttfv } = data;

  // Helper to format TTFV time
  const formatTTFV = (minutes) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    } else if (minutes < 1440) {
      const hours = Math.round(minutes / 60 * 10) / 10;
      return `${hours} hr`;
    } else {
      const days = Math.round(minutes / 1440 * 10) / 10;
      return `${days} days`;
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-analytics-tab">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Analytics & Insights
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Last updated: {new Date(data.generated_at).toLocaleString("en-IN")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchAnalytics(true)}
          disabled={refreshing}
          className="text-xs"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* ─── PRODUCT FUNNEL ───────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-6 border border-slate-200">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
            <TrendingUp className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              Product Funnel
            </h3>
            <p className="text-[10px] text-slate-500">User journey from registration to purchase</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-stretch gap-y-4" data-testid="funnel-metrics">
          <FunnelCard
            label="Registrations"
            value={funnel.registrations.toLocaleString("en-IN")}
            subLabel="CompleteRegistration"
            icon={UserPlus}
            color="bg-blue-100 text-blue-600"
          />
          <FunnelCard
            label="First Downloads"
            value={funnel.first_sample_downloads.toLocaleString("en-IN")}
            subLabel={`${funnel.registration_to_download_rate}% of registrations`}
            icon={Download}
            color="bg-indigo-100 text-indigo-600"
          />
          <FunnelCard
            label="Samples Used"
            value={funnel.free_samples_used.toLocaleString("en-IN")}
            subLabel="FreeSamplesUsed"
            icon={Image}
            color="bg-purple-100 text-purple-600"
          />
          <FunnelCard
            label="Purchases"
            value={funnel.purchases.toLocaleString("en-IN")}
            subLabel={`${funnel.download_to_purchase_rate}% of downloaders`}
            icon={ShoppingCart}
            color="bg-green-100 text-green-600"
          />
          <FunnelCard
            label="Revenue"
            value={`₹${funnel.revenue.toLocaleString("en-IN")}`}
            subLabel="Total earnings"
            icon={DollarSign}
            color="bg-emerald-100 text-emerald-600"
            isLast
          />
        </div>
      </section>

      {/* ─── USER METRICS ─────────────────────────────────────────────── */}
      <section>
        <SectionHeader title="User Metrics" icon={Users} />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <MetricCard
            label="Total Users"
            value={users.total_users.toLocaleString("en-IN")}
            icon={Users}
            color="bg-blue-50 text-blue-600"
          />
          <MetricCard
            label="Free Users"
            value={users.free_users.toLocaleString("en-IN")}
            icon={Users}
            color="bg-slate-100 text-slate-600"
          />
          <MetricCard
            label="Paid Users"
            value={users.paid_users.toLocaleString("en-IN")}
            icon={Users}
            color="bg-green-50 text-green-600"
          />
          <MetricCard
            label="Conversion Rate"
            value={`${users.conversion_rate}%`}
            subValue="Free → Paid"
            icon={TrendingUp}
            color="bg-purple-50 text-purple-600"
          />
          <MetricCard
            label="Active (7d)"
            value={users.active_users_7d.toLocaleString("en-IN")}
            subValue={`${users.new_users_7d} new this week`}
            icon={Activity}
            color="bg-amber-50 text-amber-600"
          />
        </div>
      </section>

      {/* ─── TIME TO FIRST VALUE (TTFV) ────────────────────────────────── */}
      {ttfv && (
        <section className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
              <Timer className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                Time to First Value (TTFV)
              </h3>
              <p className="text-[10px] text-slate-500">Time between registration and first successful download</p>
            </div>
          </div>
          
          {/* Primary TTFV Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="ttfv-metrics">
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ttfv-avg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Average TTFV</p>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600">
                  <Clock className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                {formatTTFV(ttfv.avg_ttfv_minutes)}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{ttfv.avg_ttfv_minutes.toFixed(1)} minutes</p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ttfv-median">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Median TTFV</p>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600">
                  <Timer className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                {formatTTFV(ttfv.median_ttfv_minutes)}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{ttfv.median_ttfv_minutes.toFixed(1)} minutes</p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ttfv-with-downloads">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Users Downloaded</p>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-100 text-green-600">
                  <Download className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                {ttfv.users_with_downloads.toLocaleString("en-IN")}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">of {ttfv.total_users_analyzed} users</p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ttfv-never-downloaded">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Never Downloaded</p>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-red-100 text-red-600">
                  <UserX className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
                {ttfv.never_downloaded_percent}%
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{ttfv.users_without_downloads} users</p>
            </div>
          </div>
          
          {/* TTFV Time Buckets */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="text-xs font-semibold text-slate-600 mb-4">First Download Timing Breakdown</h4>
            <div className="space-y-3">
              {/* Within 5 minutes */}
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600 font-medium">≤ 5 min</div>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(ttfv.within_5_min_percent, 2)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-700">
                    {ttfv.within_5_min_percent}%
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] w-20 justify-center bg-green-50 text-green-700 border-green-200">
                  Excellent
                </Badge>
              </div>
              
              {/* Within 30 minutes */}
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600 font-medium">≤ 30 min</div>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(ttfv.within_30_min_percent, 2)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-700">
                    {ttfv.within_30_min_percent}%
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] w-20 justify-center bg-blue-50 text-blue-700 border-blue-200">
                  Good
                </Badge>
              </div>
              
              {/* Within 24 hours */}
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600 font-medium">≤ 24 hr</div>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(ttfv.within_24_hr_percent, 2)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-700">
                    {ttfv.within_24_hr_percent}%
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] w-20 justify-center bg-amber-50 text-amber-700 border-amber-200">
                  Engaged
                </Badge>
              </div>
              
              {/* Never downloaded */}
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600 font-medium">Never</div>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                  <div 
                    className="h-full bg-gradient-to-r from-red-300 to-red-400 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(ttfv.never_downloaded_percent, 2)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-700">
                    {ttfv.never_downloaded_percent}%
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] w-20 justify-center bg-red-50 text-red-700 border-red-200">
                  At Risk
                </Badge>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── USAGE METRICS ────────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Usage Metrics" icon={Download} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard
            label="Total Downloads"
            value={usage.total_downloads.toLocaleString("en-IN")}
            icon={Download}
            color="bg-blue-50 text-blue-600"
          />
          <MetricCard
            label="Unique Downloads"
            value={usage.unique_downloads.toLocaleString("en-IN")}
            icon={Image}
            color="bg-indigo-50 text-indigo-600"
          />
          <MetricCard
            label="Re-downloads"
            value={usage.redownload_count.toLocaleString("en-IN")}
            icon={RefreshCw}
            color="bg-slate-100 text-slate-600"
          />
          <MetricCard
            label="Re-download Ratio"
            value={`${usage.redownload_ratio}%`}
            icon={Percent}
            color="bg-orange-50 text-orange-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <TopItemsTable
            title="Top 20 Downloaded Images"
            items={usage.top_images}
            columns={[
              { 
                label: "#", 
                key: "rank", 
                render: (_, i) => <span className="text-slate-400">{i + 1}</span>,
                className: "w-8"
              },
              { 
                label: "Dish Name", 
                key: "dish_name",
                render: (item) => (
                  <div>
                    <p className="font-medium text-[#09090B] truncate max-w-[150px]" title={item.dish_name}>
                      {item.dish_name}
                    </p>
                    <p className="text-[10px] text-slate-400">{item.cuisine || "—"}</p>
                  </div>
                )
              },
              { 
                label: "Downloads", 
                key: "download_count", 
                align: "text-right",
                className: "font-semibold text-[#09090B]"
              }
            ]}
          />
          <TopItemsTable
            title="Top Cuisines"
            items={usage.top_cuisines}
            columns={[
              { 
                label: "#", 
                key: "rank", 
                render: (_, i) => <span className="text-slate-400">{i + 1}</span>,
                className: "w-8"
              },
              { label: "Cuisine", key: "name", className: "font-medium text-[#09090B]" },
              { 
                label: "Downloads", 
                key: "download_count", 
                align: "text-right",
                className: "font-semibold"
              }
            ]}
          />
          <TopItemsTable
            title="Top Categories"
            items={usage.top_categories}
            columns={[
              { 
                label: "#", 
                key: "rank", 
                render: (_, i) => <span className="text-slate-400">{i + 1}</span>,
                className: "w-8"
              },
              { label: "Category", key: "name", className: "font-medium text-[#09090B]" },
              { 
                label: "Downloads", 
                key: "download_count", 
                align: "text-right",
                className: "font-semibold"
              }
            ]}
          />
        </div>
      </section>

      {/* ─── REVENUE METRICS ──────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Revenue Metrics" icon={DollarSign} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard
            label="Total Revenue"
            value={`₹${revenue.total_revenue.toLocaleString("en-IN")}`}
            icon={DollarSign}
            color="bg-green-50 text-green-600"
          />
          <MetricCard
            label="With Coupons"
            value={`₹${revenue.revenue_with_coupons.toLocaleString("en-IN")}`}
            subValue={`${revenue.orders_with_coupons} orders`}
            icon={Tag}
            color="bg-blue-50 text-blue-600"
          />
          <MetricCard
            label="Without Coupons"
            value={`₹${revenue.revenue_without_coupons.toLocaleString("en-IN")}`}
            subValue={`${revenue.orders_without_coupons} orders`}
            icon={DollarSign}
            color="bg-slate-100 text-slate-600"
          />
          <MetricCard
            label="Total Discount Given"
            value={`₹${revenue.total_discount_given.toLocaleString("en-IN")}`}
            icon={Percent}
            color="bg-orange-50 text-orange-600"
          />
        </div>

        <div className="max-w-md">
          <TopItemsTable
            title="Top Performing Coupons"
            items={revenue.top_coupons}
            columns={[
              { 
                label: "Coupon", 
                key: "code",
                render: (item) => (
                  <div>
                    <p className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded inline-block">{item.code}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{item.name}</p>
                  </div>
                )
              },
              { 
                label: "Uses", 
                key: "times_used", 
                align: "text-center",
                className: "font-semibold"
              },
              { 
                label: "Revenue", 
                key: "total_revenue",
                align: "text-right",
                render: (item) => <span className="text-green-600 font-medium">₹{item.total_revenue.toLocaleString("en-IN")}</span>
              },
              { 
                label: "Discount", 
                key: "total_discount",
                align: "text-right",
                render: (item) => <span className="text-orange-600">-₹{item.total_discount.toLocaleString("en-IN")}</span>
              }
            ]}
          />
        </div>
      </section>
    </div>
  );
}
